/**
 * Emma Context Renderer Component
 * Story 4.2: Displays entity search results with relevance scores
 * 
 * Renders search results from Story 1 Emma Entity Search API
 * Shows entity cards with relevance scores, types, and metadata
 */

import React, { useState } from 'react';
import { 
  Search,
  AlertCircle,
  Zap,
  File,
  Users,
  Target,
  Briefcase,
  TrendingUp,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

/**
 * Type definitions matching Story 1 API response
 */
interface EntitySearchResult {
  entity_id: string;
  entity_type: string;
  name: string;
  relevance_score: number;
  metadata?: Record<string, any>;
}

interface EmmaContextRendererProps {
  results: EntitySearchResult[];
  isLoading?: boolean;
  error?: string | null;
  entityType?: string;
  onSelectResult?: (result: EntitySearchResult) => void;
}

/**
 * Entity type configuration: icons, colors, labels
 */
const ENTITY_TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  campaign: {
    icon: <Target className="w-4 h-4" />,
    color: 'bg-blue-100 text-blue-700',
    label: 'Campaign'
  },
  content: {
    icon: <File className="w-4 h-4" />,
    color: 'bg-purple-100 text-purple-700',
    label: 'Content'
  },
  asset: {
    icon: <Briefcase className="w-4 h-4" />,
    color: 'bg-green-100 text-green-700',
    label: 'Asset'
  },
  client: {
    icon: <Users className="w-4 h-4" />,
    color: 'bg-orange-100 text-orange-700',
    label: 'Client'
  },
  partner: {
    icon: <TrendingUp className="w-4 h-4" />,
    color: 'bg-pink-100 text-pink-700',
    label: 'Partner'
  },
  solution: {
    icon: <Zap className="w-4 h-4" />,
    color: 'bg-yellow-100 text-yellow-700',
    label: 'Solution'
  },
  icp: {
    icon: <Target className="w-4 h-4" />,
    color: 'bg-cyan-100 text-cyan-700',
    label: 'ICP'
  },
  persona: {
    icon: <Users className="w-4 h-4" />,
    color: 'bg-indigo-100 text-indigo-700',
    label: 'Persona'
  }
};

/**
 * RelevanceScoreBar: Visual representation of relevance score (0-1)
 * WCAG AA compliant with sufficient color contrast
 */
const RelevanceScoreBar: React.FC<{ score: number }> = ({ score }) => {
  // Clamp score to 0-1 range
  const normalizedScore = Math.max(0, Math.min(1, score));
  const percentage = (normalizedScore * 100).toFixed(0);
  
  // Color gradient with WCAG AA contrast ratios
  // Red: 4.54:1, Yellow: 3.15:1, Green: 5.26:1
  let gradientColor = 'bg-red-600 dark:bg-red-500';
  let scoreLabel = 'Low';
  if (normalizedScore >= 0.5) {
    gradientColor = 'bg-yellow-600 dark:bg-yellow-500';
    scoreLabel = 'Medium';
  }
  if (normalizedScore >= 0.75) {
    gradientColor = 'bg-green-600 dark:bg-green-500';
    scoreLabel = 'High';
  }
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-300 dark:bg-gray-600 rounded-full h-2 overflow-hidden" role="progressbar" aria-valuenow={normalizedScore} aria-valuemin={0} aria-valuemax={1} aria-label={`Relevance score: ${percentage}%`}>
        <div
          className={`h-full transition-all duration-500 ease-out ${gradientColor}`}
          style={{ width: `${percentage}%` }}
          aria-hidden="true"
        />
      </div>
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 min-w-[2.5rem]" aria-label={`${scoreLabel} relevance: ${percentage} percent`}>
        {percentage}%
      </span>
    </div>
  );
};

/**
 * ResultCard: Individual entity result card
 * WCAG Level AA compliant with keyboard navigation and screen reader support
 */
const ResultCard: React.FC<{
  result: EntitySearchResult;
  onSelect?: (result: EntitySearchResult) => void;
}> = ({ result, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = ENTITY_TYPE_CONFIG[result.entity_type] || ENTITY_TYPE_CONFIG.campaign;
  
  const handleSelect = () => {
    onSelect?.(result);
  };
  
  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  };
  
  return (
    <div
      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md dark:hover:shadow-lg transition-all duration-300 ease-out cursor-pointer bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-900"
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-pressed={isExpanded}
      aria-label={`${config.label}: ${result.name}. Relevance score ${(result.relevance_score * 100).toFixed(0)}%. Press Enter to select.`}
    >
      {/* Header: Icon, Name, Entity Type Badge */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          {/* Entity Type Icon */}
          <div className="mt-0.5 text-gray-600 dark:text-gray-400" aria-hidden="true">
            {config.icon}
          </div>
          
          {/* Entity Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate" id={`result-name-${result.entity_id}`}>{result.name}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate" id={`result-id-${result.entity_id}`}>ID: {result.entity_id}</p>
          </div>
        </div>
        
        {/* Entity Type Badge - WCAG AA compliant colors */}
        <div className={`px-2 py-1 rounded text-xs font-medium ${config.color} dark:opacity-90 whitespace-nowrap ml-2`} aria-label={config.label}>
          {config.label}
        </div>
      </div>
      
      {/* Relevance Score */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-2">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300" htmlFor={`score-${result.entity_id}`}>Relevance</label>
          <span className="text-xs text-gray-600 dark:text-gray-400" id={`score-label-${result.entity_id}`}>{(result.relevance_score * 100).toFixed(0)}% match</span>
        </div>
        <div id={`score-${result.entity_id}`}>
          <RelevanceScoreBar score={result.relevance_score} />
        </div>
      </div>
      
      {/* Metadata Preview / Expand Toggle */}
      {result.metadata && Object.keys(result.metadata).length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleExpandToggle}
            aria-expanded={isExpanded}
            aria-controls={`metadata-${result.entity_id}`}
            className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-2 py-1"
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            )}
            <span>{isExpanded ? 'Hide' : 'Show'} details</span>
          </button>
          
          {/* Expanded Metadata */}
          {isExpanded && (
            <div className="mt-2 text-xs text-gray-700 dark:text-gray-300 space-y-1 animate-in fade-in duration-300" id={`metadata-${result.entity_id}`} role="region" aria-label="Entity details">
              {Object.entries(result.metadata).map(([key, value]) => (
                <div key={key} className="flex gap-2 py-1">
                  <span className="font-medium text-gray-800 dark:text-gray-200 min-w-[100px]">{key}:</span>
                  <span className="text-gray-700 dark:text-gray-400 break-words">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * EmptyState: Displayed when no results found
 * Accessible with proper role and semantic HTML
 */
const EmptyState: React.FC<{ entityType?: string }> = ({ entityType }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center" role="status" aria-label="No search results">
    <Search className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" aria-hidden="true" />
    <p className="text-gray-700 dark:text-gray-300 text-sm font-medium">
      {entityType ? `No ${entityType}s found` : 'No results found'}
    </p>
    <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
      Try refining your search or try a different query
    </p>
  </div>
);

/**
 * ErrorState: Displayed when search fails
 * WCAG AA compliant error messaging with proper contrast
 */
const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <div className="flex gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg" role="alert" aria-live="assertive">
    <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
    <div>
      <h4 className="text-sm font-semibold text-red-900 dark:text-red-100">Search failed</h4>
      <p className="text-xs text-red-800 dark:text-red-200 mt-1">{error}</p>
    </div>
  </div>
);

/**
 * LoadingState: Displayed while search is in progress
 * Accessible with aria-live region for status updates
 */
const LoadingState: React.FC = () => (
  <div className="space-y-2" role="status" aria-live="polite" aria-label="Loading search results">
    {[1, 2, 3].map((i) => (
      <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-100 dark:bg-gray-800 animate-pulse">
        <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-2" />
        <div className="h-2 bg-gray-300 dark:bg-gray-700 rounded w-1/2" />
      </div>
    ))}
  </div>
);

/**
 * EmmaContextRenderer: Main component
 */
export const EmmaContextRenderer: React.FC<EmmaContextRendererProps> = ({
  results = [],
  isLoading = false,
  error = null,
  entityType,
  onSelectResult
}) => {
  // Error state takes priority
  if (error) {
    return <ErrorState error={error} />;
  }
  
  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }
  
  // Empty state
  if (results.length === 0) {
    return <EmptyState entityType={entityType} />;
  }
  
  // Results state
  return (
    <div className="space-y-3" role="region" aria-label="Search results" aria-live="polite">
      {/* Results header */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
        <Search className="w-4 h-4 text-gray-600 dark:text-gray-400" aria-hidden="true" />
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
          Found {results.length} result{results.length !== 1 ? 's' : ''}
        </p>
      </div>
      
      {/* Results list */}
      <div className="space-y-3">
        {results.map((result, index) => (
          <ResultCard
            key={result.entity_id}
            result={result}
            onSelect={onSelectResult}
          />
        ))}
      </div>
    </div>
  );
};

export default EmmaContextRenderer;
